import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { MapViewButton } from './MapViewButton/MapViewButton'
import { MapViewDialog } from './MapViewDialog/MapViewDialog'
import { useData, useHandlers } from './hooks'
import {
  selectMapViewDialogCenter,
  selectMapViewDialogZoom,
  selectMapViewSource,
} from '@user/store/map/slice'
import { defaultLocation, defaultZoom } from '@user/components/HereMap/defaults'

export function MapView({ expanded }: { expanded: boolean }) {
  const data = useData()
  const { onClose, onOpen } = useHandlers(data)
  const centerFromRedux = useSelector(selectMapViewDialogCenter)
  const zoomFromRedux = useSelector(selectMapViewDialogZoom)
  const source = useSelector(selectMapViewSource)

  const [center, setCenter] = useState<H.geo.Point | undefined>()
  const [zoom, setZoom] = useState<number | undefined>()

  const tryBrowserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter(
            new H.geo.Point(
              position.coords.latitude,
              position.coords.longitude,
            ),
          )
          setZoom(defaultZoom)
        },
        () => {
          // Geolocation failed, use defaults
          setCenter(new H.geo.Point(defaultLocation.lat, defaultLocation.lng))
          setZoom(defaultZoom)
        },
      )
    } else {
      setCenter(new H.geo.Point(defaultLocation.lat, defaultLocation.lng))
      setZoom(defaultZoom)
    }
  }, [])

  // Resolve center and zoom based on source when dialog opens
  useEffect(() => {
    if (!data.open) {
      setCenter(undefined)
      setZoom(undefined)
      return
    }
    if (source === 'addressLink' && centerFromRedux) {
      // AddressMapLink already set center/zoom in Redux
      const newCenter = new H.geo.Point(
        centerFromRedux.lat,
        centerFromRedux.lng,
      )
      setCenter(newCenter)
      setZoom(zoomFromRedux)
    } else if (source === 'sidenav') {
      // Try localStorage first for sidenav clicks
      const storedCenter = localStorage.getItem('mapCenter')
      const storedZoom = localStorage.getItem('mapZoom')

      if (storedCenter && storedZoom) {
        try {
          const parsed = JSON.parse(storedCenter)
          setCenter(new H.geo.Point(parsed.lat, parsed.lng))
          setZoom(Number(storedZoom))
        } catch {
          // Fallback to browser location or defaults
          tryBrowserLocation()
        }
      } else {
        // No localStorage, try browser location
        tryBrowserLocation()
      }
    }
  }, [data.open, source, centerFromRedux, zoomFromRedux, tryBrowserLocation])

  return (
    <>
      <MapViewDialog
        open={data.open}
        onClose={onClose}
        center={center}
        zoom={zoom}
      />
      <MapViewButton onClick={onOpen} expanded={expanded} />
    </>
  )
}
